// หน้าใช้ชีวิต (docs/PLAN.md ข้อ 3.4 และ 7) หน้าตาแบบบันทึกภาคสนามของสารคดีธรรมชาติ
// หัวเป็นภาพป่ากับชื่อสัตว์และพิกัดแบบเครดิตภาพถ่าย · ซ้าย: สภาพร่างกาย สัตว์แถวนี้
// ขวา: บันทึกภาคสนามเป็นเส้นเวลา ฉากตอนนี้ และตัวเลือก หรือแผนที่ · บนมือถือเรียงลงมาเป็นแนวตั้ง

import { useEffect, useRef, useState } from "react";
import { CONTENT } from "../../content/events";
import { heroPhoto } from "../../data/hero-photos";
import { choose, currentStage, migrationStatus, presentEvent, stagesOf } from "../../engine/life";
import { displayName, formatAge } from "../../engine/text";
import type { LifeState, Neighbor, Stats } from "../../engine/types";
import { MONTH_TH } from "../../geo/migration";
import { formatCoords, placeLabel } from "../../geo/place";
import type { SpeciesRange } from "../../geo/range";
import type { SpawnResult } from "../../shared/api";
import { GeoPanel } from "../components/GeoPanel";
import type { GeoState } from "../geo";
import { logIcon, subgroupIcon } from "../icons";
import { groupNames, offspringLabel } from "../names";

interface Props {
  life: LifeState;
  spawn: SpawnResult;
  geo: GeoState;
  range: SpeciesRange | null;
  advance: (life: LifeState) => LifeState; // ย้ายตำแหน่งบนแผนที่หลังจบ turn
  onChange: (life: LifeState) => void;
  onFinished: () => void;
}

const STAT_BARS: [keyof Stats, string][] = [
  ["fullness", "ความอิ่ม"],
  ["health", "สุขภาพ"],
  ["safety", "ความปลอดภัย"],
];

const tone = (v: number) => (v >= 60 ? "good" : v >= 30 ? "mid" : "bad");

function Bar({ label, value, className }: { label: string; value: number; className: string }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="field-stat">
      <span className="field-stat-label">{label}</span>
      <span className="field-stat-value">{Math.round(v)}</span>
      <span className="field-bar" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v)}>
        <span className={`field-bar-fill ${className}`} style={{ width: `${v}%` }} />
      </span>
    </div>
  );
}

function Specimen({ n, role }: { n: Neighbor; role: "predator" | "prey" }) {
  return (
    <li className={`specimen ${role}`} title={n.note ?? n.sci}>
      <span className="specimen-icon" aria-hidden>
        {subgroupIcon(n.subgroupId)}
      </span>
      <span className="specimen-name">
        {n.nameTh ?? n.sci}
        <i>{n.sci}</i>
      </span>
    </li>
  );
}

export function Life({ life, spawn, geo, range, advance, onChange, onFinished }: Props) {
  // ข้อความที่เพิ่งเกิดจากการเลือกครั้งล่าสุด จะค่อยๆ ปรากฏในบันทึก
  const [freshFrom, setFreshFrom] = useState(() => Math.max(0, life.log.length - 3));
  const [view, setView] = useState<"log" | "map">("log");
  const sceneRef = useRef<HTMLDivElement>(null);
  const logRef = useRef<HTMLOListElement>(null);
  const event = presentEvent(life, CONTENT);
  const stage = currentStage(life);
  const stages = stagesOf(life.species);
  const migration = life.alive ? migrationStatus(life) : null;
  const neighbors = life.neighbors;
  const names = groupNames(life.species.subgroupId);
  const ready = geo.status === "ready" ? geo.data : null;
  // ภาพถ่ายจริงของประเภทพื้นที่ที่อยู่ตอนนี้ (ไม่มีภาพก็ใช้ภาพป่าที่วาดด้วย CSS)
  const photo = heroPhoto(life.geo?.biome, life.geo?.mangrove);

  // บันทึกเลื่อนไปท้ายสุด และบนมือถือเลื่อนจอให้เห็นฉากตอนนี้
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
    if (life.turn > 0) sceneRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [life.turn, life.alive]);

  function pick(index: number) {
    const next = advance(choose(life, index, CONTENT));
    setFreshFrom(life.log.length);
    onChange(next);
  }

  return (
    <main className="field">
      {/* ---------- หัว: ชื่อสัตว์บนภาพป่า ---------- */}
      <header
        className={photo ? "field-hero has-photo" : "field-hero"}
        style={photo ? ({ "--hero-photo": `url("${photo.file}")` } as React.CSSProperties) : undefined}
      >
        <p className="kicker">
          บันทึกภาคสนาม{life.lineage ? ` · รุ่นที่ ${life.lineage.generation}` : ""} · {names.group}
        </p>
        <h1 className="field-title">{displayName(life.species)}</h1>
        <p className="field-sci">{life.species.sci}</p>
        <p className="field-caption">
          <span>
            อายุ <b>{formatAge(life.ageDays)}</b> · {stage.nameTh}
          </span>
          {life.geo && ready && (
            <span>
              {placeLabel(ready.grid, life.geo)} · {formatCoords(life.geo.at)}
            </span>
          )}
        </p>
        {photo && (
          <a className="field-credit" href={photo.source} target="_blank" rel="noopener noreferrer">
            ภาพ: {photo.artist} · {photo.license} · Wikimedia Commons
          </a>
        )}
      </header>

      <div className="field-grid">
        {/* ---------- สภาพร่างกาย ---------- */}
        <aside className="field-side" aria-label="สภาพของคุณ">
          <section className="field-panel body">
            <p className="kicker">สภาพร่างกาย</p>
            <div className="field-stats">
              {STAT_BARS.map(([key, label]) => (
                <Bar key={key} label={label} value={life.stats[key]} className={tone(life.stats[key])} />
              ))}
              <Bar label="ช่วงชีวิต" value={(life.ageDays / (life.species.traits.lifespanYears * 365)) * 100} className="life" />
            </div>
            <ol className="field-stages" aria-label={`ช่วงวัยที่ ${life.stageIndex + 1} จาก ${stages.length}`}>
              {stages.map((s, i) => (
                <li key={s.id} className={i < life.stageIndex ? "done" : i === life.stageIndex ? "now" : ""}>
                  <span />
                  {s.nameTh}
                </li>
              ))}
            </ol>
            <dl className="field-facts">
              <div>
                <dt>{offspringLabel(life.flags)}</dt>
                <dd>{life.offspring.toLocaleString("th-TH")}</dd>
              </div>
              {life.geo && life.geo.km >= 1 && (
                <div>
                  <dt>เดินทางมาแล้ว</dt>
                  <dd>{Math.round(life.geo.km).toLocaleString("th-TH")} กม.</dd>
                </div>
              )}
              {migration && (
                <div>
                  <dt>ตอนนี้</dt>
                  <dd>เดือน{MONTH_TH[migration.month - 1]}</dd>
                </div>
              )}
              {life.lineage && (
                <div>
                  <dt>สายตระกูล</dt>
                  <dd>รุ่นที่ {life.lineage.generation}</dd>
                </div>
              )}
            </dl>
          </section>

          {neighbors && (neighbors.predators.length > 0 || neighbors.prey.length > 0) && (
            <section className="field-panel near">
              <p className="kicker">สัตว์ที่พบแถวนี้</p>
              {neighbors.predators.length > 0 && (
                <>
                  <p className="field-label predator">ระวัง: นักล่า</p>
                  <ul className="specimens">
                    {neighbors.predators.slice(0, 4).map((n) => (
                      <Specimen key={n.sci} n={n} role="predator" />
                    ))}
                  </ul>
                </>
              )}
              {neighbors.prey.length > 0 && (
                <>
                  <p className="field-label prey">อาหาร</p>
                  <ul className="specimens">
                    {neighbors.prey.slice(0, 4).map((n) => (
                      <Specimen key={n.sci} n={n} role="prey" />
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}
        </aside>

        {/* ---------- บันทึก ฉากตอนนี้ และตัวเลือก ---------- */}
        <section className="field-main">
          <div className="field-tabs" role="tablist">
            <button role="tab" aria-selected={view === "log"} className={view === "log" ? "on" : ""} onClick={() => setView("log")}>
              บันทึกภาคสนาม
            </button>
            <button role="tab" aria-selected={view === "map"} className={view === "map" ? "on" : ""} onClick={() => setView("map")}>
              แผนที่
            </button>
          </div>

          {view === "map" ? (
            <GeoPanel geo={geo} card={spawn} range={range} track={life.geo ?? null} mode="now" neighbors={life.neighbors} life={life} />
          ) : (
            <ol className="journal" ref={logRef} aria-live="polite">
              {life.log.map((e, i) => (
                <li key={i} className={i >= freshFrom ? "fresh" : ""}>
                  <span className="journal-dot" aria-hidden>
                    {logIcon(e.text, life.species.subgroupId)}
                  </span>
                  <span className="journal-age">{formatAge(e.ageDays)}</span>
                  <span className="journal-text">{e.text}</span>
                </li>
              ))}
            </ol>
          )}

          <div className="scene" ref={sceneRef}>
            {event ? (
              <>
                <p className="kicker">{event.signature ? `เหตุการณ์เฉพาะของ${displayName(life.species)}` : "ตอนนี้"}</p>
                <p className="event-text">{event.text}</p>
                {event.fact && (
                  <p className="field-note">
                    <b>รู้ไหม</b> {event.fact}
                  </p>
                )}
                <div className="actions scene-choices">
                  {event.choices.map((label, i) => (
                    <button key={`${event.id}-${life.turn}-${i}`} className={`btn block scene-choice${i === 0 ? " first" : ""}`} onClick={() => pick(i)}>
                      <span className="scene-choice-n">{String.fromCharCode(65 + i)}</span>
                      {label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="kicker">จบการบันทึก</p>
                <p className="event-text">ชีวิตของคุณจบลงแล้ว</p>
                <div className="actions scene-choices">
                  <button className="btn primary block" onClick={onFinished}>
                    ดูสรุปชีวิต
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
